import { useState } from 'react';

export default function SystemOverview() {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const systemComponents = [
    {
      icon: 'ri-water-flash-line',
      title: '水循環システム',
      description: '高度な濾過技術により、水を繰り返し使用。新鮮な水の使用量を従来の1/10以下に削減します。',
      features: ['生物濾過', '物理濾過', '化学濾過']
    },
    {
      icon: 'ri-temp-hot-line',
      title: '温度管理',
      description: '最適な水温を年間通じて維持。季節に左右されない安定した生産環境を実現します。',
      features: ['自動温度調整', '省エネ設計', '24時間監視']
    },
    {
      icon: 'ri-bubble-chart-line',
      title: '酸素供給',
      description: '効率的な酸素供給システムにより、魚の健康と成長を最適化します。',
      features: ['純酸素供給', '溶存酸素管理', 'エアレーション']
    },
    {
      icon: 'ri-shield-check-line',
      title: '水質管理',
      description: 'リアルタイムで水質をモニタリング。常に最適な飼育環境を維持します。',
      features: ['pH管理', '窒素化合物除去', '水質センサー']
    }
  ];

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#E8F5E9] mb-6">
            <i className="ri-settings-3-line text-[#2D7A3E] text-lg mr-2"></i>
            <span className="text-[#2D7A3E] text-sm font-semibold whitespace-nowrap">システム構成</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1A2332] mb-6">
            閉鎖循環型養殖システムの仕組み
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            水を循環利用することで環境負荷を最小限に抑え、<br className="hidden md:block" />
            安定した高品質な水産物の生産を実現します。
          </p>
        </div>

        <div className="mb-20">
          <div className="rounded-2xl overflow-hidden shadow-2xl relative">
            <img
              src="https://storage.readdy-site.link/project_files/fc18929a-f6b4-4a9e-92ee-da32c9ea052d/3a37d875-9d77-4d0f-91b0-97bfb1581692_BDB85AA55E70D1D0AC57EE09F06E10861F3262D6.jpg?v=7a439271e48d8a030ec9d0e0a335a5e2"
              alt="閉鎖循環型養殖システムの構成図"
              className="w-full h-[220px] md:h-[600px] object-cover object-top"
            />
            {/* Full screen button */}
            <button
              onClick={() => setIsFullscreen(true)}
              className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm text-white text-xs font-medium px-3 py-2 rounded-full cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-fullscreen-line text-sm"></i>
              </div>
              全体を見る
            </button>
          </div>
        </div>

        {/* Fullscreen Modal */}
        {isFullscreen && (
          <div
            className="fixed inset-0 z-50 bg-black flex items-center justify-center"
            onClick={() => setIsFullscreen(false)}
          >
            <button
              onClick={() => setIsFullscreen(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full text-white cursor-pointer z-10"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
            <img
              src="https://storage.readdy-site.link/project_files/fc18929a-f6b4-4a9e-92ee-da32c9ea052d/3a37d875-9d77-4d0f-91b0-97bfb1581692_BDB85AA55E70D1D0AC57EE09F06E10861F3262D6.jpg?v=7a439271e48d8a030ec9d0e0a335a5e2"
              alt="閉鎖循環型養殖システムの構成図"
              className="w-full h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {systemComponents.map((component, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-[#F5F9F6] to-white p-8 rounded-2xl border border-[#E8F5E9] hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-start mb-6">
                <div className="w-14 h-14 bg-[#2D7A3E] rounded-lg flex items-center justify-center mr-5 flex-shrink-0">
                  <i className={`${component.icon} text-white text-2xl`}></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#1A2332] mb-3">{component.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">{component.description}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {component.features.map((feature, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 bg-white rounded-full text-sm text-[#2D7A3E] font-semibold border border-[#C8E6C9] whitespace-nowrap"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-r from-[#2D7A3E] to-[#4CAF50] p-12 rounded-2xl text-white">
          <div className="text-center mb-10">
            <h3 className="text-3xl font-bold mb-4">システムの主な特徴</h3>
            <p className="text-lg opacity-95 leading-relaxed">
              環境に配慮しながら、高品質な水産物を安定的に生産できる先進的なシステムです。
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-recycle-line text-white text-2xl"></i>
              </div>
              <p className="text-2xl font-bold mb-2">90%以上</p>
              <p className="text-sm opacity-90">水の再利用率</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-drop-line text-white text-2xl"></i>
              </div>
              <p className="text-2xl font-bold mb-2">ゼロ</p>
              <p className="text-sm opacity-90">環境への排水</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-check-line text-white text-2xl"></i>
              </div>
              <p className="text-2xl font-bold mb-2">365日</p>
              <p className="text-sm opacity-90">年間安定生産</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-pulse-line text-white text-2xl"></i>
              </div>
              <p className="text-2xl font-bold mb-2">高品質</p>
              <p className="text-sm opacity-90">健康な水産物</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}